import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { configureStore } from '@reduxjs/toolkit';
import { createReduxEnhancer } from '@sentry/react';
import { routerMiddleware } from 'connected-react-router';
import constant from 'lodash/constant';
import type { TypedUseSelectorHook } from 'react-redux';
import {
  useDispatch as useRealReduxDispatch, // eslint-disable-line no-restricted-imports
  useSelector as useRealReduxSelector, // eslint-disable-line no-restricted-imports
  // This is the only spot in the whole repository that this import is allowed
} from 'react-redux';
import { persistStore } from 'redux-persist';
import thunk from 'redux-thunk';

import buildClient from 'src/app/client/consumer-web';
import HISTORY from 'src/app/router/history';
import { clientRegister, clientStart, segmentAnalyticsPage } from 'src/app/state/actions';
import historyChangeListenerMiddleware from 'src/app/state/historyChange';
import getInitialState from 'src/app/state/initial';
import segmentMiddleware from 'src/app/state/segment';

import type { ExtendedReducerState } from './reducers';
import rootReducer from './reducers';

function initRedux(initialState = {}) {
  const store = configureStore<ExtendedReducerState, any, any>({
    reducer: rootReducer,
    middleware: [thunk, routerMiddleware(HISTORY), segmentMiddleware, historyChangeListenerMiddleware],
    enhancers: [
      createReduxEnhancer({
        /**
         * By always returning an empty object, we never actually show the Redux Store in Sentry to avoid overtly
         * logging PCI/PII data. We _are_ exposing breadcrumbs which may contain PCI/PII, but - after consulting with
         * the security team - this was deemed a risk worth taking on given the useful debugging information provided.
         */
        stateTransformer: constant({}),
      }),
    ],
    preloadedState: initialState,
  });

  const client = buildClient({ store });

  const persistor = persistStore(store, undefined, () => {
    store.dispatch(clientRegister(client));
    store.dispatch(clientStart());
    store.dispatch(segmentAnalyticsPage());
  });

  return { store, persistor, client };
}

const { store, persistor, client } = initRedux(getInitialState());

export { store, persistor, client };

/**
 * The type below the comment is synonymous with the following:
 * @type {ReturnType<ReturnType<typeof createStore>['getState']>}
 *
 * Leaving it typed as below for compiler speed benefit.
 */
export type RootState = ExtendedReducerState;

export type Dispatch = ThunkDispatch<RootState, undefined, AnyAction>;

export const useDispatch: () => Dispatch = useRealReduxDispatch;

export const useSelector: TypedUseSelectorHook<RootState> = useRealReduxSelector;
